import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Gift, X } from 'lucide-react';
import { toast } from 'sonner';
import walletService from '../../services/walletService';

const CreditWalletModal = ({ user, onClose, onCredited, complaintId = null }) => {
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const parsedAmount = Number(amount);
    if (!parsedAmount || parsedAmount < 1) {
      toast.error('Enter a valid amount (minimum ₹1)');
      return;
    }
    setLoading(true);
    try {
      const result = await walletService.adminCreditWallet(user.id, parsedAmount, note.trim(), complaintId);
      if (result.success) {
        toast.success(`₹${parsedAmount} BMF Coins credited to ${user.name}`);
        onCredited?.(result.data);
        onClose();
      } else {
        toast.error(result.error || 'Failed to credit BMF Coins');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl p-6 max-w-md w-full">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-gray-800">Credit BMF Coins</h3>
            <p className="text-sm text-gray-500">Add BMF Coins to <span className="font-medium text-gray-700">{user.name}</span>'s account</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg flex-shrink-0">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mb-4 text-sm text-amber-700">
          These points are credited directly — no real payment is charged. They appear in the user's BMF Coins history as <strong>Admin Points</strong>.
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Amount (₹) *</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium">₹</span>
              <input
                type="number"
                min="1"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="Enter amount"
                className="w-full pl-8 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#548B2E]"
                required
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Note (optional)</label>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Loyalty reward, Complaint resolution…"
              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#548B2E]"
            />
          </div>
          <div className="flex gap-3 justify-end pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !amount}
              className="px-5 py-2 bg-[#548B2E] text-white rounded-lg hover:bg-[#3f6b20] disabled:opacity-50 flex items-center gap-2"
            >
              {loading
                ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                : <Gift className="w-4 h-4" />}
              Credit Points
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

CreditWalletModal.propTypes = {
  user: PropTypes.shape({ id: PropTypes.number, name: PropTypes.string }).isRequired,
  onClose: PropTypes.func.isRequired,
  onCredited: PropTypes.func,
  complaintId: PropTypes.number,
};

export default CreditWalletModal;
